import React, { useState, useMemo } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { Tooltip } from 'react-tooltip';
import { indiaJson } from "../constants/india";
import { feature } from "topojson-client";

const INDIA_GEO = feature(indiaJson, indiaJson.objects.india);

export default function TopRegionsMap({
    salesData = {},
    scale = 500,
    width = 360,
    height = 420,
    bottomLabel = true,
}) {
    const [tooltipContent, setTooltipContent] = useState("");

    const maxValue = Math.max(...Object.values(salesData), 1);
    const colorScale = useMemo(
        () =>
            scaleLinear()
                .domain([0, maxValue * 0.5, maxValue])
                .range(["#E8F8EF", "#38C76F", "#145F3B"]),
        [maxValue]
    );

    return (
        <>
            <div className="relative">
                <ComposableMap
                    projection="geoMercator"
                    projectionConfig={{ scale, center: [82.8, 22.5] }}
                    width={width}
                    height={height}
                    style={{ width: "100%", height: "auto" }}
                >
                    <Geographies geography={INDIA_GEO}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const name = geo.properties.name;
                                const value = salesData[name] || 0;
                                const formattedValue = value.toLocaleString();

                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill={colorScale(value)}
                                        stroke="#FFFFFF"
                                        strokeWidth={0.25}
                                        style={{
                                            default: { outline: "none", transition: "all 0.3s ease" },
                                            hover: {
                                                strokeWidth: 1.2,
                                                stroke: "#FFD700",
                                                cursor: "pointer",
                                                filter: "brightness(1.1)",
                                            },
                                            pressed: { strokeWidth: 1.5, stroke: "#FFA500" },
                                        }}
                                        onMouseEnter={() => {
                                            setTooltipContent(`${name}: ₹${formattedValue}`);
                                        }}
                                        onMouseLeave={() => {
                                            setTooltipContent("");
                                        }}
                                        data-tooltip-id="region-tooltip"
                                        data-tooltip-content={tooltipContent}
                                    />
                                );
                            })
                        }
                    </Geographies>
                </ComposableMap>

                <Tooltip
                    id="region-tooltip"
                    place="top"
                    opacity={0.95}
                    style={{
                        backgroundColor: "#1F2937",
                        color: "#FFFFFF",
                        fontSize: "12px",
                        fontWeight: "600",
                        borderRadius: "6px",
                        padding: "6px 10px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        zIndex: 1000,
                    }}
                    render={({ content }) => <div>{content}</div>}
                />
            </div>

            {bottomLabel && (
                <div className="mt-4 px-3">
                    <div className="text-xs font-medium text-gray-700 mb-1">Sales Volume</div>
                    <div className="flex items-center gap-2">
                        <span>0</span>
                        <div
                            className="h-3 flex-grow rounded-full bg-gradient-to-r from-[#E8F8EF] via-[#38C76F] to-[#145F3B] shadow-sm"
                            style={{ boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)" }}
                        />
                        <span className="mt-1">{maxValue.toLocaleString()}</span>
                    </div>
                </div>
            )}
        </>
    );
}